import React, { useEffect, useRef, useState } from 'react';
import cc from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AxiosStatus } from 'api/axiosConfig';
import api from 'api/api';
import { IApiResponse } from 'utils/models';
import LoaderPage from 'ui/LoaderPage/LoaderPage';
import ErrorPage from 'ui/ErrorPage/ErrorPage';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import TablePagination from 'ui/TableInputs/TablePagination/TablePagination';
import LoaderForTable from 'ui/LoaderForTable/LoaderForTable';
import { amountFormatter } from 'utils/formatters';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import NavigateBackBtn from 'ui/NavigateBackBtn/NavigateBackBtn';
import { IApiPendingBulkTransfer, IPendingTransfer } from '../../Transfers.model';

interface IPendingBulkDetails {
  isOpen: boolean;
  data: IPendingTransfer;
  onCloseTable: () => void;
}

const PendingBulkDetails = ({ isOpen, data, onCloseTable }: IPendingBulkDetails) => {
  const [loadingStatus, setLoadingStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const pendingLoading = loadingStatus === AxiosStatus.PENDING;
  const successLoaded = loadingStatus === AxiosStatus.SUCCESS;
  const failureLoaded = loadingStatus === AxiosStatus.FAILURE;
  const gridRef = useRef<AgGridReact<IPendingTransfer>>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalRows, setTotalRows] = useState(0);

  const [rowData, setRowData] = useState<IPendingTransfer[]>([]);

  // TODO fix double request for first render to display NoData component
  useEffect(() => {
    setLoadingStatus(AxiosStatus.PENDING);
    const requestBody = {
      bulkId: data.bulkId,
      pageSize,
      pageNumber,
    };
    api
      .post('transfer/transactionDrillDownByBulkId', requestBody)
      .then((datas: IApiPendingBulkTransfer) => {
        const formattedData = datas.transactions;
        setRowData(formattedData);
        setTotalRows(datas.totalCount);
        setLoadingStatus(AxiosStatus.SUCCESS);
      })
      .catch((error: IApiResponse) => {
        setLoadingStatus(AxiosStatus.FAILURE);
        console.error(error);
      });
  }, [data.bulkId, pageNumber, pageSize]);

  const renderAmount = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data && prop.data?.transactionAmount) {
      return <div>{amountFormatter('ghs', false)(String(prop.data?.transactionAmount))}</div>;
    }
    return null;
  };

  const renderNarration = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data) {
      return <div>{prop.data?.transactionNarration}</div>;
    }
    return null;
  };

  const renderStatus = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data) {
      return <div>{prop.data?.transactionStatus ? '' : 'Pending'}</div>;
    }
    return null;
  };

  const renderTransType = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data && prop.data?.transactionType) {
      return <div>{prop.data?.transactionType}</div>;
    }
    return null;
  };
  const renderBeneficiaryName = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data && prop.data?.beneficiaryName) {
      return <div>{prop.data.beneficiaryName}</div>;
    }
    return null;
  };

  const renderBeneficiaryAccount = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data && prop.data?.beneficiaryAccount) {
      return <div>{prop.data.beneficiaryAccount}</div>;
    }
    return null;
  };

  const renderBeneficiaryBank = (prop: ICellRendererParams<IPendingTransfer>) => {
    if (prop.data && prop.data?.beneficiaryBank) {
      return <div>{prop.data.beneficiaryBank}</div>;
    }
    return null;
  };
  const columnDefs: ColDef[] = [
    {
      field: 'beneficiaryName',
      headerName: 'Beneficiary Name',
      flex: 1,
      minWidth: 150,
      cellRenderer: renderBeneficiaryName,
    },
    {
      field: 'beneficiaryAccount',
      headerName: 'Beneficiary Account',
      flex: 1,
      minWidth: 150,
      cellRenderer: renderBeneficiaryAccount,
    },
    {
      field: 'beneficiaryBank',
      headerName: 'Beneficiary Bank',
      flex: 1,
      minWidth: 150,
      cellRenderer: renderBeneficiaryBank,
    },
    { field: 'amount', headerName: 'Amount', flex: 1, minWidth: 130, cellRenderer: renderAmount },
    { field: 'transType', headerName: 'Transaction Type', flex: 1, minWidth: 150, cellRenderer: renderTransType },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200, cellRenderer: renderNarration },
    { field: 'status', headerName: 'Status', width: 220, cellRenderer: renderStatus },
  ];

  // Pagination & Filters handlers
  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPageSize = parseInt(event.target.value as string, 10);
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  return (
    <>
      {pendingLoading && <LoaderPage />}
      {failureLoaded && <ErrorPage />}
      {successLoaded && isOpen && (
        <>
          {/* <pre>{JSON.stringify(rowData, null, 2)}</pre> */}
          <DashboardCard>
            <NavigateBackBtn onBackBtnClick={onCloseTable}>Back</NavigateBackBtn>
            <NavigationTitle to="#/" layoutType={LayoutType.dashboard} onClick={onCloseTable}>
              Bulk Transfers List
            </NavigationTitle>
            <div className={cc('tableWrapper', 'ag-theme-material')}>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                loadingOverlayComponent={LoaderForTable}
                columnDefs={columnDefs}
                defaultColDef={{ resizable: true }}
                suppressCellFocus
                enableCellTextSelection
                headerHeight={60}
                rowHeight={70}
                undoRedoCellEditing
                suppressRowHoverHighlight
                pagination
                paginationPageSize={pageSize}
                suppressPaginationPanel
                suppressScrollOnNewData
                domLayout="autoHeight"
              />
            </div>
            <TablePagination
              count={Math.ceil(totalRows / pageSize)}
              page={pageNumber}
              pageSize={pageSize}
              totalCount={totalRows}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
            />
          </DashboardCard>
        </>
      )}
    </>
  );
};

export default PendingBulkDetails;
